<template>
  <FocusTrap :active="open">
    <div
      id="mobile-nav"
      class="m-mobile-nav"
      :class="{
        'm-mobile-nav--open': open,
        'm-mobile-nav--mounted': mounted,
      }"
    >
      <div class="m-mobile-nav__overlay" @click.self="$emit('close')" />
      <div class="m-mobile-nav__nav">
        <div class="m-mobile-nav__top">
          <div class="m-mobile-nav__close">
            <MButtonIcon
              ref="closeMenuButtonIcon"
              type="button"
              aria-label="Close menu"
              icon="x"
              size="h2"
              :aria-expanded="open"
              aria-controls="mobile-nav"
              @click="$emit('close')"
            />
          </div>

          <div class="m-mobile-nav__list m-mobile-nav__list--top">
            <slot name="top" />
          </div>
        </div>

        <MSkin v-if="!!$slots.bottom" class="m-mobile-nav__skin" skin="medium">
          <div class="m-mobile-nav__bottom">
            <MHeading
              v-if="bottomHeader"
              class="m-mobile-nav__header"
              tag="div"
              size="h5"
            >
              <span class="m-mobile-nav__dot" />
              {{ bottomHeader }}
            </MHeading>
            <div class="m-mobile-nav__list">
              <slot name="bottom" />
            </div>
          </div>
        </MSkin>
      </div>
    </div>
  </FocusTrap>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue'

import MButtonIcon from '../../controls/ButtonIcon/MButtonIcon'
import MSkin from '../../skins/Skin/MSkin'
import MHeading from '../../typography/Heading/MHeading'

/**
 * The site mobile navigation drawer
 */
export default {
  name: 'MMobileNav',
  components: { FocusTrap, MButtonIcon, MSkin, MHeading },
  props: {
    /**
     * Title for the bottom section
     */
    bottomHeader: {
      type: String,
      default: '',
    },
    /**
     * Whether the nav is open or closed
     */
    open: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {
    return {
      mounted: false,
    }
  },
  watch: {
    open(newValue) {
      const bodyClasses = document.querySelector('body').classList
      if (newValue) {
        bodyClasses.add('body--mobile-nav-open')
        this.$nextTick().then(() => {
          this.$refs.closeMenuButtonIcon?.$el.focus()
        })
      } else {
        bodyClasses.remove('body--mobile-nav-open')
        document.activeElement.blur()
      }
    },
  },
  mounted() {
    this.mounted = true
  },
}
</script>

<style lang="scss">
.body--mobile-nav-open {
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/breakpoint';

@keyframes fadeout {
  99% {
    visibility: visible;
  }
  100% {
    visibility: hidden;
  }
}

@keyframes fadein {
  0% {
    visibility: hidden;
  }
  1% {
    visibility: visible;
  }
}

.m-mobile-nav {
  // Placeholder for body class to show bodyOverlay
  &__overlay {
    background: var(--overlay-background);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: var(--z-index-modal-overlay);

    .m-mobile-nav--mounted & {
      transition: opacity var(--animation);
      transition-delay: calc(var(--animation-speed) / 2);
    }

    .m-mobile-nav--open & {
      opacity: 0.9;
      pointer-events: auto;
      transition-delay: 0s;
    }
  }

  &__nav {
    animation: var(--animation-speed) fadeout;
    animation-fill-mode: both;
    background-color: var(--skin-background-light);
    bottom: 0;
    box-shadow: var(--skin-background-dark) 1px 1px 3px;
    display: flex;
    flex-direction: column;
    float: right;
    height: 100%;
    max-width: 267px;
    overflow-y: scroll;
    position: fixed;
    right: 0;
    top: 0;
    transform: translateX(100%);
    width: 100%;
    z-index: calc(var(--z-index-modal-overlay) + 10);

    @include breakpoint.breakpoint(tablet-up) {
      max-width: 372px;
    }

    .m-mobile-nav--mounted & {
      transition: transform var(--animation);
      transition-delay: 0s;
    }

    .m-mobile-nav--open & {
      animation: var(--animation-speed) fadein;
      animation-delay: calc(var(--animation-speed) / 2);
      transform: translateX(0);
      transition-delay: calc(var(--animation-speed) / 2);
    }
  }

  &__skin {
    flex: 1 1 auto;
  }

  &__top {
    background-color: var(--skin-background);
  }

  &__bottom {
    background-color: var(--skin-background);
    height: 100%;
  }

  &__close {
    float: right;
    margin-bottom: var(--spacing-24);
    padding: var(--spacing-24) var(--spacing-32);
  }

  &__header {
    border-bottom: 1px solid var(--skin-separator-color);
    margin: 0;
    padding: calc(var(--spacing-24) - var(--spacing-4)) var(--spacing-24);
  }

  &__dot {
    background-color: var(--state-success-dark);
    border-radius: 50%;
    display: inline-block;
    height: 8px;
    margin: 0 var(--spacing-12) 2px 0;
    width: 8px;
  }

  &__list {
    color: var(--color-text-body-dark);
    list-style-type: none;
    margin: 0;
    padding-left: 0;

    &--top {
      border-top: 1px solid var(--skin-separator-color);
      margin-top: var(--spacing-80);
    }
  }
}
</style>
